import React from 'react';
import { cn } from '@/lib/cn';
import { TAG_CONFIGS, type TagVariant } from '@repo/utils';
import { SpriteIcon } from '@/components/ui/IconSprite';
import type { SpriteIconName } from '@/components/ui/iconSpriteData';

// Tag is the single biggest source of repeated inline SVG in the app: it draws
// a badge icon AND a chevron for every tag, and listing grids render ~142 tags
// per page. Both go through the sprite so the geometry ships once per document
// rather than 284 times. See components/ui/IconSprite.tsx.
const SPRITE_BY_VARIANT: Record<TagVariant, SpriteIconName> = {
  verified: 'verified',
  topRated: 'top-rated',
  rentStabilized: 'rent-stabilized',
  goodCause: 'good-cause',
  future: 'future',
  worstLandlord: 'worst',
};

export interface TagProps extends React.HTMLAttributes<HTMLDivElement> {
  /** The variant determines the icon and label displayed */
  variant?: TagVariant;
  /** Whether the tag should have an opaque background with border (true) or semi-transparent background with shadow (false) */
  opaque?: boolean;
  linkable?: boolean;
}

export function Tag({
  variant = 'verified',
  opaque = false,
  linkable = true,
  className,
  ...props
}: TagProps) {
  const config = TAG_CONFIGS[variant];
  const label = config.label;

  const baseStyles = cn(
    // Layout and positioning
    'flex items-center gap-1',
    'rounded-full',
    'p-2', // 8px padding
    'cursor-pointer select-none',
    'transition-all duration-200 ease-in-out',

    // Conditional styling based on opaque prop
    opaque
      ? // Opaque version (white background with border)
        'bg-white border border-secondary hover:bg-bg-primary-hover'
      : // Semi-transparent version (with shadow)
        'bg-white-90 hover:bg-white shadow hover:bg-bg-primary',
  );

  return (
    <div className={cn(baseStyles, className)} {...props}>
      {/* Icon - 16px */}
      <div className="w-4 h-4 flex-shrink-0">
        <SpriteIcon name={SPRITE_BY_VARIANT[variant]} />
      </div>

      {/* Label - 12px semibold */}
      <span className="text-caption-high text-primary whitespace-nowrap">{label}</span>

      {/* Arrow - 8px */}
      {linkable && (
        <div className="w-2 h-2 flex-shrink-0">
          <SpriteIcon name="chevron-right" className="w-full h-full icon-primary" />
        </div>
      )}
    </div>
  );
}
